import * as React from "react";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "../../lib/utils";

const ButtonGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    orientation?: "horizontal" | "vertical";
  }
>(({ className, orientation = "horizontal", ...props }, ref) => (
  <div
    ref={ref}
    role="group"
    data-orientation={orientation}
    className={cn(
      "inline-flex rounded-lg border border-input bg-background shadow-sm shadow-black/5",
      "data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch",
      "[&>button]:relative [&>button]:border-0",
      "[&>button:focus-visible]:z-10",
      "[&>button]:rounded-none [&>button:first-child]:rounded-s-[calc(var(--radius)-1px)] [&>button:last-child]:rounded-e-[calc(var(--radius)-1px)]",
      "data-[orientation=vertical]:[&>button:first-child]:rounded-t-[calc(var(--radius)-1px)] data-[orientation=vertical]:[&>button:last-child]:rounded-b-[calc(var(--radius)-1px)]",
      "data-[orientation=vertical]:[&>button:first-child]:rounded-b-none data-[orientation=vertical]:[&>button:last-child]:rounded-t-none",
      "[&>button:not(:first-child)]:-ms-px [&>button:not(:last-child)]:border-e",
      "data-[orientation=vertical]:[&>button:not(:first-child)]:mt-0 data-[orientation=vertical]:[&>button:not(:first-child)]:-mt-px data-[orientation=vertical]:[&>button:not(:last-child)]:border-e-0 data-[orientation=vertical]:[&>button:not(:last-child)]:border-b",
      className
    )}
    {...props}
  />
));
ButtonGroup.displayName = "ButtonGroup";

const ButtonGroupSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    orientation?: "horizontal" | "vertical";
  }
>(({ className, orientation = "vertical", ...props }, ref) => (
  <div
    ref={ref}
    data-orientation={orientation}
    className={cn(
      "bg-border",
      orientation === "vertical"
        ? "h-full w-px data-[orientation=vertical]:w-px data-[orientation=vertical]:h-full"
        : "w-full h-px data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full",
      className
    )}
    {...props}
  />
));
ButtonGroupSeparator.displayName = "ButtonGroupSeparator";

const ButtonGroupText = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    asChild?: boolean;
  }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "div";
  return (
    <Comp
      ref={ref}
      className={cn(
        "inline-flex h-9 items-center whitespace-nowrap px-3 text-sm font-medium text-muted-foreground [&:has(+button)]:-me-px [button+&]:-ms-px",
        className
      )}
      {...props}
    />
  );
});
ButtonGroupText.displayName = "ButtonGroupText";

export { ButtonGroup, ButtonGroupSeparator, ButtonGroupText };
